import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Button, ValidationMessage, Input } from "components";
import { StepState } from "helpers";
import { confirmResetPassword } from "aws-amplify/auth";
import { useTranslation } from "react-i18next";

interface AuthPasswordRecoverFormI {
  code: string;
  password: string;
  repeatPassword: string;
}

const initialData: AuthPasswordRecoverFormI = {
  code: "",
  password: "",
  repeatPassword: "",
};

interface AuthPasswordRecoverFormProps extends Pick<StepState, "setStep"> {
  email?: string;
  code?: string;
}

export const AuthPasswordRecoverForm = ({ setStep, email = "", code = "" }: AuthPasswordRecoverFormProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<AuthPasswordRecoverFormI>({
    ...initialData,
    code: code || initialData.code,
  });
  const [codeError, setCodeError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [generalError, setGeneralError] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setDisabled(!data.code || !data.password || !data.repeatPassword || !!codeError || !!passwordError);
  }, [data, codeError, passwordError]);

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { name, value },
    } = event;

    setData((oldObject: AuthPasswordRecoverFormI) => ({
      ...oldObject,
      [name]: value,
    }));
  };

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    setDisabled(true);
    setIsLoading(true);
    resetError();

    if (data.password !== data.repeatPassword) {
      setPasswordError(t("auth.passwordMismatch"));
      setDisabled(false);
      return;
    }

    if (!email) {
      setGeneralError(t("auth.emailMissing"));
      setIsLoading(false);
      setDisabled(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: data.code,
        newPassword: data.password,
      });
      setData(initialData);
      setStep(1);
    } catch (err: any) {
      const name = err?.name as string | undefined;
      const message = (err?.message as string | undefined) ?? t("auth.passwordResetError");
      if (
        name === "CodeMismatchException" ||
        name === "ExpiredCodeException" ||
        message.toLowerCase().includes("code")
      ) {
        setCodeError(message);
      } else if (name === "InvalidPasswordException" || message.toLowerCase().includes("password")) {
        setPasswordError(message);
      } else {
        setGeneralError(message);
      }
    } finally {
      setIsLoading(false);
      setDisabled(false);
    }
  };

  const resetError = () => {
    setCodeError("");
    setPasswordError("");
    setGeneralError("");
  };

  return (
    <form onSubmit={onSubmitHandler} autoComplete="off">
      <div className="space-y-8">
        <div className="flex flex-col">
          <div className="space-y-5">
            <Input
              id="code"
              name="code"
              value={data.code}
              onChange={onChangeHandler}
              error={!!codeError}
              maxLength={6}
              minLength={6}
              placeholder={t("auth.codePlaceholder")}
              autoComplete="one-time-code"
            />
            <Input
              id="password"
              name="password"
              type="password"
              value={data.password}
              onChange={onChangeHandler}
              error={!!passwordError}
              placeholder={t("auth.newPassword")}
              autoComplete="new-password"
            />
            <Input
              id="repeatPassword"
              name="repeatPassword"
              type="password"
              value={data.repeatPassword}
              onChange={onChangeHandler}
              error={!!passwordError}
              placeholder={t("auth.repeatPassword")}
              autoComplete="new-password"
            />
          </div>
          <ValidationMessage>{codeError}</ValidationMessage>
          <ValidationMessage>{passwordError}</ValidationMessage>
          <ValidationMessage>{generalError}</ValidationMessage>
        </div>
        <div className="flex justify-center">
          <Button type="submit" disabled={disabled || isLoading}>
            {isLoading ? t("auth.loading") : t("auth.resetPassword")}
          </Button>
        </div>
      </div>
    </form>
  );
};
